import { useState, useEffect, useRef } from "react";
import { Info } from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTagsInfinite } from "../../services/tags";
import { FormField, TextInput, TextArea } from "../ui/FormField";
import ImageUpload from "../ui/ImageUpload";

const today = new Date().toISOString().split("T")[0];

const offerSchema = z
  .object({
    name: z.string().min(1, "Please enter an offer name"),
    description: z.string().min(1, "Please enter a description"),
    tags: z.array(z.number()),
    start_date: z
      .string()
      .min(1, "Please select a start date")
      .refine((v) => v >= today, "Start date must be today or in the future"),
    expiry_date: z.string().min(1, "Please select an expiry date"),
    redeemable_only_once: z.boolean(),
  })
  .refine((data) => data.expiry_date > data.start_date, {
    message: "Expiry date must be after the start date",
    path: ["expiry_date"],
  });

type OfferSchema = z.infer<typeof offerSchema>;

export interface OfferFormValues {
  name: string;
  description: string;
  tags: number[];
  start_date: string;
  expiry_date: string;
  image_url: string;
  redeemable_only_once: boolean;
}

interface OfferFormProps {
  initialValues?: Partial<OfferFormValues>;
  onSave: (values: OfferFormValues) => void;
  onReset?: () => void;
  isSubmitting?: boolean;
}

const OfferForm = ({ initialValues, onSave, onReset, isSubmitting = false }: OfferFormProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [imageUrl, setImageUrl] = useState(initialValues?.image_url ?? "");
  const [imageError, setImageError] = useState("");
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { data: tagsData, fetchNextPage, hasNextPage, isFetchingNextPage } = useTagsInfinite();
  const tags = tagsData?.pages.flatMap((p) => p.tags) ?? [];

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm<OfferSchema>({
    resolver: zodResolver(offerSchema),
    defaultValues: {
      name: initialValues?.name ?? "",
      description: initialValues?.description ?? "",
      tags: initialValues?.tags ?? [],
      start_date: initialValues?.start_date ?? "",
      expiry_date: initialValues?.expiry_date ?? "",
      redeemable_only_once: initialValues?.redeemable_only_once ?? false,
    },
  });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const onSubmit = (data: OfferSchema) => {
    if (!imageUrl) {
      setImageError("Image is required");
      return;
    }
    onSave({
      ...data,
      start_date: new Date(data.start_date).toISOString(),
      expiry_date: new Date(data.expiry_date).toISOString(),
      image_url: imageUrl,
      redeemable_only_once: data.redeemable_only_once,
    });
  };

  const handleReset = () => {
    setImageUrl(initialValues?.image_url ?? "");
    setImageError("");
    setUploadKey((k) => k + 1);
    reset({
      name: initialValues?.name ?? "",
      description: initialValues?.description ?? "",
      tags: initialValues?.tags ?? [],
      start_date: initialValues?.start_date ?? "",
      expiry_date: initialValues?.expiry_date ?? "",
      redeemable_only_once: initialValues?.redeemable_only_once ?? false,
    });
    onReset?.();
  };


  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="bg-white rounded-[8px] px-[33px] pt-[33px] pb-[49px] flex flex-col gap-6"
      style={{
        border: "1px solid rgba(194,199,201,0.1)",
        boxShadow: "0px 20px 25px -5px rgba(0,0,0,0.1), 0px 8px 10px -6px rgba(0,0,0,0.1)",
      }}
    >
      <FormField label="Offer Name" error={errors.name?.message}>
        <TextInput {...register("name")} placeholder="e.g. Birthday Bonus" />
      </FormField>

      <FormField label="Offer Description" error={errors.description?.message}>
        <TextArea {...register("description")} placeholder="Describe the offer…" rows={3} />
      </FormField>

      {/* Dietary Preference */}
      <FormField label="Dietary Preference" error={errors.tags?.root?.message ?? errors.tags?.message}>
        <Controller
          name="tags"
          control={control}
          render={({ field }) => {
            const selected: number[] = field.value ?? [];
            const toggle = (id: number) => {
              const next = selected.includes(id)
                ? selected.filter((v) => v !== id)
                : [...selected, id];
              field.onChange(next);
            };
            return (
              <div ref={dropdownRef} className="relative">
                <button
                  type="button"
                  onClick={() => setDropdownOpen((o) => !o)}
                  className="w-full rounded-[6px] px-4 py-[13px] text-[14px] font-normal text-left flex items-center justify-between gap-2 bg-surface-input text-content-body"
                >
                  {selected.length === 0 ? (
                    <span className="text-content-muted">Select dietary restrictions…</span>
                  ) : (
                    <span className="flex flex-wrap gap-1">
                      {selected.map((id) => {
                        const tag = tags.find((t) => Number(t.id) === id);
                        return (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] font-medium text-white bg-brand-primary"
                          >
                            {tag?.name ?? id}
                            <span
                              role="button"
                              onClick={(e) => { e.stopPropagation(); toggle(id); }}
                              className="leading-none hover:opacity-70 cursor-pointer"
                            >
                              ×
                            </span>
                          </span>
                        );
                      })}
                    </span>
                  )}
                  <svg
                    className={`w-4 h-4 flex-shrink-0 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                    fill="none" stroke="#42484a" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {dropdownOpen && (
                  <div
                    className="absolute z-10 w-full mt-1 rounded-[6px] overflow-y-auto max-h-[200px] bg-surface-input"
                    style={{ boxShadow: "0px 8px 20px rgba(0,0,0,0.12)" }}
                    onScroll={(e) => {
                      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                      if (scrollHeight - scrollTop - clientHeight < 50 && hasNextPage && !isFetchingNextPage) {
                        fetchNextPage();
                      }
                    }}
                  >
                    {!tags.length ? (
                      <p className="px-4 py-3 text-[13px] text-content-muted">No tags available.</p>
                    ) : (
                      <>
                        {tags.map((tag) => {
                          const checked = selected.includes(Number(tag.id));
                          return (
                            <label
                              key={tag.id}
                              className="flex items-center gap-3 px-4 py-[10px] cursor-pointer hover:bg-black/5 transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggle(Number(tag.id))}
                                className="w-4 h-4 rounded accent-[#023a36]"
                              />
                              <span className="text-[14px] text-content-body">{tag.name}</span>
                            </label>
                          );
                        })}
                        {isFetchingNextPage && (
                          <p className="px-4 py-2 text-[12px] text-content-muted">Loading…</p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          }}
        />
      </FormField>

      <label className="flex items-center gap-3 cursor-pointer group">
        <input
          type="checkbox"
          {...register("redeemable_only_once")}
          className="w-4 h-4 rounded accent-[#023a36] cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#023a36] focus:ring-offset-1"
        />
        <span className="text-[14px] font-medium text-content-body group-hover:text-brand-primary transition-colors">
          One time redeemable
        </span>
        <div className="relative flex items-center group/tooltip">
          <Info className="w-[14px] h-[14px] text-content-muted cursor-pointer shrink-0" />
          <div className="absolute left-5 top-1/2 -translate-y-1/2 z-20 hidden group-hover/tooltip:block w-max max-w-[200px] rounded-[6px] bg-content-heading px-3 py-2 text-[12px] text-white shadow-lg">
            Each customer is eligible to redeem this offer only once.
          </div>
        </div>
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Start Date" error={errors.start_date?.message}>
          <TextInput type="date" {...register("start_date")} min={today} />
        </FormField>
        <FormField label="Expiry Date" error={errors.expiry_date?.message}>
          <TextInput type="date" {...register("expiry_date")} min={watch("start_date") || today} />
        </FormField>
      </div>

      <ImageUpload
        key={uploadKey}
        label="Offer Image"
        initialUrl={initialValues?.image_url}
        onKeyChange={(key) => { setImageUrl(key); if (key) setImageError(""); }}
        onBusyChange={setIsImageUploading}
      />
      {imageError && <span className="text-[11px] text-content-error">{imageError}</span>}

      <p className="text-[13px] text-content-label leading-[1.6] rounded-[6px] bg-orange-50 px-4 py-3">
        <span className="font-semibold text-content-body">Note:</span> Creating an offer does not automatically send it to customers. To notify customers, please click the <span className="font-semibold text-content-body">Send Email</span> button after creating the offer.
      </p>

      <div className="flex flex-col gap-3 pt-4">
        <button
          type="submit"
          disabled={isSubmitting || isImageUploading}
          className="w-full h-[45px] rounded-[20px] text-white text-[16px] font-bold tracking-[0.64px] transition-opacity hover:opacity-90 disabled:opacity-60 bg-brand-accent"
        >
          {isSubmitting ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={handleReset}
          className="w-full py-3 rounded-[6px] text-[14px] font-semibold text-center transition-opacity hover:opacity-70 text-brand-teal"
        >
          Reset Changes
        </button>
      </div>
    </form>
  );
};

export default OfferForm;
